// =============================================================================
// timetable-events.js – DOM event bindings for the Timetable tab
// =============================================================================
// Extracted from timetable.js to separate rendering from event handling.
// =============================================================================

import { getDayType, getScheduleKey, resolveDirection, escapeHtml, getAppDate } from "./utils.js";
import { openMap } from "./map.js";
import { getVisibleStops, getAllStopsOrdered, realignStopsToRoute } from "./timetable-data.js";
import { getStopName } from "./line-config.js";
import {
  buildTimetableSearchResults,
  findLineServingStop,
  lineServesStop,
  pushSearchHistory
} from "./timetable-search.js";
import { togglePinnedTrip, setPinnedTrip } from "./timetable-pin.js";

/**
 * Bind timetable interaction events.
 * @param {HTMLElement} container
 * @param {object} ctx - { getArgs, renderTimetable }
 * @param {Function} [saveSettingsParam] - optional override for saveSettings
 */
export function bindTimetableEvents(container, ctx, saveSettingsParam) {
  const { getArgs, renderTimetable } = ctx;
  const { state, lineData, lineConfig, cfg, saveSettings: lastSaveSettings } = getArgs();
  const saveSettings = saveSettingsParam || lastSaveSettings;

  container.querySelectorAll("[data-line]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", async () => {
      const lineId = button.dataset.line;
      if (!lineData[lineId] && lineConfig[lineId]) {
        try {
          const { loadSingleLine } = await import("./line-registry.js");
          const data = await loadSingleLine(lineId);
          if (data) lineData[lineId] = data;
        } catch (e) {
          console.error(`[Trasporti] Errore caricamento ${lineId}:`, e);
        }
      }
      // Edge case: switching line mid-edit would otherwise persist the
      // previous line's stop codes into the new line's settings keys on
      // Salva. Exit edit mode on any context switch (line/day/direction).
      exitEditModeIfActive(state);
      state.timetableLine = lineId;
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  });

  container.querySelectorAll("[data-day]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", () => {
      exitEditModeIfActive(state);
      state.timetableDayType = button.dataset.day;
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  });

  // V1: Andata/Ritorno direction toggle. The Figma markup is
  // `<button class="direction-toggle" data-dir="...">` directly inside
  // `.filter-row`, with NO `[data-timetable-direction]` ancestor — so the
  // old selector `[data-timetable-direction] [data-dir]` never matched and
  // the toggle was dead (the ⇅ swap button masked it). Bind directly.
  container.querySelectorAll(".direction-toggle[data-dir]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", () => {
      exitEditModeIfActive(state);
      state.timetableDirection = button.dataset.dir;
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  });

  container.querySelectorAll("th.stop-link[data-stop-code]").forEach((th) => {
    if (th.__has_map_click) return;
    th.__has_map_click = true;
    th.addEventListener("click", () => {
      if (state.timetableEditMode) return;
      openMap(th.dataset.stopCode);
    });
  });

  const premiumBtn = container.querySelector("#toggle-all-stops");
  if (premiumBtn && !premiumBtn.__has_click) {
    premiumBtn.__has_click = true;
    premiumBtn.addEventListener("click", () => {
      state.showAllStops = !state.showAllStops;
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  }

  const toggleAllLinesBtn = container.querySelector("[data-toggle-timetable-all-lines]");
  if (toggleAllLinesBtn && !toggleAllLinesBtn.__has_click) {
    toggleAllLinesBtn.__has_click = true;
    toggleAllLinesBtn.addEventListener("click", async () => {
      state.timetableShowAllLines = !state.timetableShowAllLines;
      if (state.timetableShowAllLines) {
        try {
          const { loadLines, getCachedLineData } = await import("./line-registry.js");
          await loadLines(Object.keys(lineConfig));
          Object.assign(lineData, getCachedLineData());
        } catch (e) {
          console.error("[Trasporti] Errore caricamento tutte le linee:", e);
        }
      }
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  }

  const editBtn = container.querySelector("#edit-timetable-stops");
  if (editBtn && !editBtn.__has_click) {
    editBtn.__has_click = true;
    editBtn.addEventListener("click", () => {
      const activeLine = state.timetableLine || cfg.lineOrder?.[0] || "Z649";
      const dayType = state.timetableDayType || getDayType(new Date(), cfg);
      const baseDirection = state.timetableDirection || "outbound";
      const direction = resolveDirection(baseDirection, state.settings);
      const scheduleKey = getScheduleKey(activeLine, dayType, direction);

      const currentStops =
        state.settings?.timetableStops?.[activeLine]?.[scheduleKey] ||
        state.settings?.timetableStops?.[activeLine]?.[direction] ||
        getVisibleStops(
          state,
          cfg,
          activeLine,
          scheduleKey,
          direction,
          lineConfig[activeLine]?.referenceStops || []
        );

      state.timetableTempStops = [...currentStops];
      state.timetableEditMode = true;
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  }

  const cancelBtn = container.querySelector("#cancel-timetable-stops");
  if (cancelBtn && !cancelBtn.__has_click) {
    cancelBtn.__has_click = true;
    cancelBtn.addEventListener("click", () => {
      state.timetableEditMode = false;
      delete state.timetableTempStops;
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  }

  const saveBtn = container.querySelector("#save-timetable-stops");
  if (saveBtn && !saveBtn.__has_click) {
    saveBtn.__has_click = true;
    saveBtn.addEventListener("click", () => {
      const timetableStops = structuredClone(state.settings.timetableStops || {});
      const activeLine = state.timetableLine || cfg.lineOrder?.[0] || "Z649";
      const baseDirection = state.timetableDirection || "outbound";
      const direction = resolveDirection(baseDirection, state.settings);
      if (!timetableStops[activeLine]) timetableStops[activeLine] = {};
      for (const dt of ["weekday", "saturday", "sunday"]) {
        const key = getScheduleKey(activeLine, dt, direction);
        timetableStops[activeLine][key] = [...state.timetableTempStops];
      }
      state.timetableEditMode = false;
      delete state.timetableTempStops;
      if (saveSettings) saveSettings({ timetableStops });
      else renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  }

  container.querySelectorAll("th.edit-col[data-edit-stop-code]").forEach((th) => {
    if (th.__has_edit_click) return;
    th.__has_edit_click = true;
    th.addEventListener("click", (e) => {
      e.stopPropagation();
      e.preventDefault();
      e.stopImmediatePropagation();
      const code = th.dataset.editStopCode;
      const idx = state.timetableTempStops.indexOf(code);
      if (idx > -1) {
        if (state.timetableTempStops.length <= 1) {
          alert("La tabella deve contenere almeno una fermata visibile.");
          return;
        }
        state.timetableTempStops.splice(idx, 1);
      } else {
        state.timetableTempStops.push(code);
        // Bug-fix: previously push() always appended the newly-added column
        // at the end of the table, ignoring the line's route order. Realign
        // to the direction's stop order so the column lands in its correct
        // traversal position (and any previously-saved out-of-order entries
        // get fixed as a side effect).
        const activeLine = state.timetableLine || cfg.lineOrder?.[0] || "Z649";
        const baseDirection = state.timetableDirection || "outbound";
        const direction = resolveDirection(baseDirection, state.settings);
        const dirTrips = [];
        for (const dt of ["weekday", "saturday", "sunday"]) {
          const k = getScheduleKey(activeLine, dt, direction);
          for (const t of lineData[activeLine]?.[k] || []) dirTrips.push(t);
        }
        const refStops = getAllStopsOrdered(dirTrips);
        state.timetableTempStops = realignStopsToRoute(state.timetableTempStops, refStops);
      }
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  });

  // ── Search (città / linea / fermata / via) ──────────────────────────────
  // Reuses .stop-search-wrapper / .search-result-item styling from the LIVE
  // tab. Picking a line switches the active line; picking a stop either sets
  // it as Da (when the active line serves it) or switches to the first line
  // (among the city's lines) that serves it and then sets it as Da.
  const searchInput = container.querySelector("[data-timetable-search-input]");
  const searchResults = container.querySelector("[data-timetable-search-results]");
  if (searchInput && searchResults) {
    const refreshResults = (q) => {
      searchResults.innerHTML = buildTimetableSearchResults(q, {
        lineConfig,
        activeLine: state.timetableLine || cfg.lineOrder?.[0] || null
      });
      searchResults.classList.toggle("open", !!q && q.length >= 1);
      searchResults.querySelectorAll("[data-tt-line-pick], [data-tt-stop-pick]").forEach((btn) => {
        if (btn.__has_click) return;
        btn.__has_click = true;
        btn.addEventListener("click", () => {
          if (btn.dataset.ttLinePick) {
            const lineId = btn.dataset.ttLinePick;
            exitEditModeIfActive(state);
            if (!lineData[lineId] && lineConfig[lineId]) {
              import("./line-registry.js")
                .then(({ loadSingleLine }) => loadSingleLine(lineId))
                .then((data) => {
                  if (data) lineData[lineId] = data;
                  state.timetableLine = lineId;
                  renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
                })
                .catch((e) => console.error(`[Trasporti] Errore caricamento ${lineId}:`, e));
            } else {
              state.timetableLine = lineId;
              renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
            }
            searchInput.value = "";
            searchResults.classList.remove("open");
            searchResults.innerHTML = "";
          } else if (btn.dataset.ttStopPick) {
            const stopCode = btn.dataset.ttStopPick;
            handleStopPick(stopCode);
            searchInput.value = "";
            searchResults.classList.remove("open");
            searchResults.innerHTML = "";
          }
        });
      });
    };

    const handleStopPick = async (stopCode) => {
      pushSearchHistory(stopCode);
      const activeLine = state.timetableLine || cfg.lineOrder?.[0] || "Z649";
      const baseDirection = state.timetableDirection || "outbound";
      const direction = resolveDirection(baseDirection, state.settings);
      const dayType = state.timetableDayType || getDayType(getAppDate(), cfg);

      // If the active line serves this stop, set it as Da directly.
      if (lineServesStop(activeLine, stopCode, lineData, direction)) {
        applyFromStop(activeLine, stopCode);
        return;
      }

      // Otherwise, find the first candidate line (city lines first, then
      // all known) that serves the stop. Lazy-load when needed.
      const cityLines = cfg.lineOrder || Object.keys(lineConfig);
      let target = findLineServingStop(stopCode, cityLines, lineData, dayType, baseDirection, state.settings);
      if (!target) {
        // Try loading all lines then re-check.
        try {
          const { loadLines, getCachedLineData } = await import("./line-registry.js");
          await loadLines(Object.keys(lineConfig));
          Object.assign(lineData, getCachedLineData());
        } catch (e) {
          console.error("[Trasporti] Errore caricamento linee:", e);
        }
        target = findLineServingStop(stopCode, Object.keys(lineConfig), lineData, dayType, baseDirection, state.settings);
      }
      if (!target) {
        console.warn(`[Orari] Nessuna linea serve la fermata ${stopCode}`);
        return;
      }
      state.timetableLine = target;
      // Make sure the line's data is loaded before applying as Da.
      if (!lineData[target]) {
        try {
          const { loadSingleLine } = await import("./line-registry.js");
          const data = await loadSingleLine(target);
          if (data) lineData[target] = data;
        } catch (e) {
          console.error(`[Trasporti] Errore caricamento ${target}:`, e);
        }
      }
      applyFromStop(target, stopCode);
    };

    /** Set `code` as the FROM (Da) stop for `lineId`, writing into the same
     *  timetableStops keys the FROM sheet uses (so the rest of the flow is
     *  consistent). Then re-render. */
    const applyFromStop = (lineId, code) => {
      exitEditModeIfActive(state);
      const baseDirection = state.timetableDirection || "outbound";
      const direction = resolveDirection(baseDirection, state.settings);
      const visibleStops = getVisibleStops(state, cfg, lineId, getScheduleKey(lineId, state.timetableDayType || getDayType(getAppDate(), cfg), direction), direction, lineConfig[lineId]?.referenceStops || []);
      const timetableStops = { ...(state.settings?.timetableStops || {}) };
      if (!timetableStops[lineId]) timetableStops[lineId] = {};
      for (const dt of ["weekday", "saturday", "sunday"]) {
        const key = getScheduleKey(lineId, dt, direction);
        const current = timetableStops[lineId][key] || [...visibleStops];
        timetableStops[lineId][key] = [code, ...current.filter((c) => c !== code)];
      }
      // Re-validate the current A against the new FROM — same guard as S5.
      if (state.timetableToStop) {
        const routeStops = getAllStopsOrdered(lineData[lineId]?.[getScheduleKey(lineId, state.timetableDayType || getDayType(getAppDate(), cfg), direction)] || []);
        const fromIdx = routeStops.indexOf(code);
        const toIdx = routeStops.indexOf(state.timetableToStop);
        if (toIdx === -1 || toIdx <= fromIdx) delete state.timetableToStop;
      }
      if (saveSettings) saveSettings({ timetableStops });
      else renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    };

    if (!searchInput.__has_input) {
      searchInput.__has_input = true;
      searchInput.addEventListener("input", () => {
        const q = searchInput.value.trim();
        if (q.length >= 1) refreshResults(q);
        else {
          searchResults.innerHTML = "";
          searchResults.classList.remove("open");
        }
      });
      // Close dropdown on outside click.
      searchInput.addEventListener("blur", () => {
        // Defer to allow click on result item to fire first.
        setTimeout(() => {
          if (!document.activeElement?.closest("[data-timetable-search]")) {
            searchResults.classList.remove("open");
          }
        }, 150);
      });
      searchInput.addEventListener("focus", () => {
        if (searchInput.value.trim().length >= 1) refreshResults(searchInput.value.trim());
      });
    }
  }

  const toggleEmptyStopsBtn = container.querySelector("#toggle-empty-stops");
  if (toggleEmptyStopsBtn && !toggleEmptyStopsBtn.__has_click) {
    toggleEmptyStopsBtn.__has_click = true;
    toggleEmptyStopsBtn.addEventListener("click", () => {
      state.showEmptyStops = !state.showEmptyStops;
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  }

  // P10: use container-level delegation for the Card|Tabella view-toggle so
  // it works regardless of whether the rendered element survives patchDOM.
  if (!container.__has_view_toggle_delegation) {
    container.__has_view_toggle_delegation = true;
    container.addEventListener("click", (e) => {
      const segBtn = e.target.closest('#toggle-classic-view button[data-view], .view-toggle button[data-view]');
      if (!segBtn) return;
      const view = segBtn.dataset.view;
      const willBeClassic = view === "table";
      if (willBeClassic === state.timetableClassicView) return;

      // Smart default for table view: pre-seed timetableStops[lineId][scheduleKey]
      // with [from, to] so the classic table shows just the two columns the
      // user is currently comparing.
      // S3: previously used `Object.values(lineData[activeLine]).flat()` (all
      // directions × day-types) to find candidate stops — that often picked
      // wrong-direction codes (e.g. [`PG111`, `MD001`] on Z649 sunday_outbound),
      // which yielded zero non-empty columns AND got persisted via saveSettings
      // + timetableClassicView in localStorage → permanently broken tab on
      // reload. Now seeds only from CURRENT-direction route order, and never
      // persists a pair that produces zero non-empty columns.
      if (willBeClassic) {
        const activeLine = state.timetableLine || cfg.lineOrder?.[0] || "Z649";
        const dayType = state.timetableDayType || getDayType(new Date(), cfg);
        const baseDirection = state.timetableDirection || "outbound";
        const direction = resolveDirection(baseDirection, state.settings);
        const scheduleKey = getScheduleKey(activeLine, dayType, direction);
        const existing =
          state.settings?.timetableStops?.[activeLine]?.[scheduleKey] ||
          state.settings?.timetableStops?.[activeLine]?.[direction];
        if (!existing) {
          const dirTrips = lineData[activeLine]?.[scheduleKey] || [];
          const routeStops = getAllStopsOrdered(dirTrips);
          if (routeStops.length > 0) {
            const fromStop = routeStops[0];
            // Honor the current A only if it's valid for this direction and
            // strictly after FROM; otherwise fall back to the last stop.
            const validToStop =
              state.timetableToStop &&
              routeStops.indexOf(state.timetableToStop) > 0
                ? state.timetableToStop
                : routeStops[routeStops.length - 1];
            const stopsList =
              fromStop === validToStop
                ? [fromStop]
                : [fromStop, validToStop].filter(Boolean);
            // Never persist a pair that yields zero non-empty columns —
            // that was the root cause of the permanent breakage.
            const hasNonEmpty = dirTrips.some(
              (t) =>
                stopsList.some((c) => t.stops && t.stops[c] != null)
            );
            if (hasNonEmpty) {
              const timetableStops = structuredClone(
                state.settings?.timetableStops || {}
              );
              if (!timetableStops[activeLine]) timetableStops[activeLine] = {};
              timetableStops[activeLine][scheduleKey] = stopsList;
              if (saveSettings) saveSettings({ timetableStops });
            }
          }
        }
      }

      state.timetableClassicView = willBeClassic;
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(
          "trasporti_timetable_classic_view",
          String(state.timetableClassicView)
        );
      }
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  }

  const togglePastTripsBtn = container.querySelector("#toggle-past-trips");
  if (togglePastTripsBtn && !togglePastTripsBtn.__has_click) {
    togglePastTripsBtn.__has_click = true;
    togglePastTripsBtn.addEventListener("click", () => {
      // Canonical contract (main): showPastTrips gates whether past trips are
      // RENDERED at all (flat view) and forces clusters open (groups view).
      // Both require a full re-render; a display-only toggle can't express it.
      state.showPastTrips = !state.showPastTrips;
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  }

  // "Prossime corse" strip: tapping an entry expands its trip card and
  // scrolls it into view. Expansion persists via state.timetableExpandedTrips.
  container.querySelectorAll("[data-next-dep]").forEach((btn) => {
    if (btn.__has_next_dep_click) return;
    btn.__has_next_dep_click = true;
    btn.addEventListener("click", () => {
      const tripId = btn.dataset.nextDep;
      if (!tripId) return;
      if (!state.timetableExpandedTrips) state.timetableExpandedTrips = {};
      state.timetableExpandedTrips[tripId] = true;
      const card = container.querySelector(`.trip-card[data-trip-id="${CSS.escape(tripId)}"]`);
      if (!card) return;
      card.classList.add("expanded");
      card.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  });
  container.querySelectorAll("[data-timetable-filter-window] [data-window]").forEach((button) => {
    if (button.__has_click) return;
    button.__has_click = true;
    button.addEventListener("click", () => {
      const windowVal = button.dataset.window;
      state.timetableFilterWindow = windowVal === "all" ? "all" : parseInt(windowVal, 10);
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("trasporti_timetable_filter_window", windowVal);
      }
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  });

  // "Segui questa corsa" — pin/unpin a trip. Lives on .trip-pin-btn inside
  // .right-stack of trip-cards and the first cell of table rows. State is
  // persisted via state.timetablePinnedTrip + localStorage.
  container.querySelectorAll(".trip-pin-btn[data-trip-pin]").forEach((btn) => {
    if (btn.__has_pin_click) return;
    btn.__has_pin_click = true;
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      e.preventDefault();
      const tripId = btn.dataset.tripPin;
      const lineId = btn.dataset.line;
      const dayType = btn.dataset.day;
      const direction = btn.dataset.dir;
      if (!tripId || !lineId || !dayType || !direction) return;
      togglePinnedTrip(state, { lineId, dayType, direction, tripId });
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  });

  // Pinned-trip banner ✕ button (clear pin from the top banner).
  const pinClearBtn = container.querySelector("[data-pinned-trip-clear]");
  if (pinClearBtn && !pinClearBtn.__has_click) {
    pinClearBtn.__has_click = true;
    pinClearBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      setPinnedTrip(state, null);
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  }

  // Click the banner body (not ✕) → switch the user's line/day/direction back
  // to the pinned trip's context and expand its card. Same UX as "jump to".
  const pinBanner = container.querySelector("[data-pinned-trip-banner]");
  if (pinBanner && !pinBanner.__has_click) {
    pinBanner.__has_click = true;
    pinBanner.addEventListener("click", (e) => {
      // Ignore clicks on the ✕ (handled above) and the inner banner buttons.
      if (e.target.closest("[data-pinned-trip-clear]")) return;
      const pin = state.timetablePinnedTrip;
      if (!pin) return;
      // Lazy-load the line if needed, then switch context.
      const doSwitch = () => {
        exitEditModeIfActive(state);
        state.timetableLine = pin.lineId;
        state.timetableDayType = pin.dayType;
        state.timetableDirection = pin.direction === "outbound" ? "outbound" : "return";
        // Expand + scroll to the trip after the next render. Use the existing
        // state.timetableExpandedTrips map (V2) so .expanded survives re-render.
        if (!state.timetableExpandedTrips) state.timetableExpandedTrips = {};
        state.timetableExpandedTrips[String(pin.tripId)] = true;
        // Suppress the auto-scroll-to-current-trip for one render so we land
        // on the pinned card instead.
        state._timetableLastScrollSignature = null;
        renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
        // Scroll the pinned card into view after patchDOM completes.
        requestAnimationFrame(() => {
          const card = container.querySelector(
            `.trip-card[data-trip-id="${CSS.escape(String(pin.tripId))}"], tr[data-trip-id="${CSS.escape(String(pin.tripId))}"]`
          );
          if (card && typeof card.scrollIntoView === "function") {
            card.scrollIntoView({ block: "center", behavior: "smooth" });
          }
        });
      };
      if (!lineData[pin.lineId] && lineConfig[pin.lineId]) {
        import("./line-registry.js")
          .then(({ loadSingleLine }) => loadSingleLine(pin.lineId))
          .then((data) => { if (data) lineData[pin.lineId] = data; })
          .then(doSwitch)
          .catch((err) => console.error(`[Trasporti] Errore caricamento ${pin.lineId}:`, err));
      } else {
        doSwitch();
      }
    });
  }

  container.querySelectorAll("tr[data-trip-id], .trip-card[data-trip-id]").forEach((row) => {
    if (row.__has_expand_click) return;
    row.__has_expand_click = true;
    row.addEventListener("click", (e) => {
      // Don't toggle expansion when the click hits an inner interactive element
      // (stop pins, transfer pills, swap buttons, edit buttons, links).
      if (
        e.target.closest("button") ||
        e.target.closest("a") ||
        e.target.closest(".pin-btn, .pin-mini, .from-to-swap-btn, .ss-swap, .rfi-link, .leave-hint") ||
        state.timetableEditMode
      ) return;
      const tripId = row.dataset.tripId;
      const isCard = row.classList.contains("trip-card");
      // P7: Figma-aligned modern card expand toggles .expanded class only;
      // CSS handles max-height transition on .trip-expand. For legacy
      // classic-table rows we keep the display:none/block toggle.
      // V2: persist expansion in state so the 60s auto-refresh re-applies
      // the .expanded class (patchDOM would otherwise strip it, collapsing
      // every expanded card on every tick).
      if (isCard) {
        const willOpen = !row.classList.contains("expanded");
        row.classList.toggle("expanded");
        if (!state.timetableExpandedTrips) state.timetableExpandedTrips = {};
        if (willOpen) state.timetableExpandedTrips[tripId] = true;
        else delete state.timetableExpandedTrips[tripId];
        return;
      }
      container.querySelectorAll("tr.expand-row").forEach((exRow) => {
        if (exRow.id !== `expand-${tripId}`) {
          exRow.style.display = "none";
        }
      });
      const expandRow = container.querySelector(`tr#expand-${tripId}`);
      if (expandRow) {
        const willOpen = expandRow.style.display === "none";
        expandRow.style.display = willOpen ? "table-row" : "none";
      }
    });
  });

  // Phase 2 of feature/figma-alignment: stop pins (both the larger .pin-btn
  // in the Da/A selector and the smaller .pin-mini inside expanded stop
  // rows) open the existing floating #map-overlay via openMap(stopCode).
  container.querySelectorAll(".pin-btn[data-stop-code], .pin-mini[data-stop-code]").forEach((pin) => {
    if (pin.__has_pin_click) return;
    pin.__has_pin_click = true;
    pin.addEventListener("click", (e) => {
      e.stopPropagation();
      if (state.timetableEditMode) return;
      const code = pin.dataset.stopCode;
      if (code) openMap(code);
    });
  });

  // ── Da → A: Stop Selection & Swap Triggers ──────────────────────────────────
  // P10: use container-level event delegation so this is robust against
  // patchDOM re-creating the .ss-row div (the __has_click flag pattern was
  // unreliable when patch's reconcile preserved the element but stripped
  // JS-side flags, or vice versa). Note: .ss-row is a <div role="button">
  // so that the inner .pin-btn button is valid HTML.
  if (!container.__has_ss_delegation) {
    container.__has_ss_delegation = true;
    container.addEventListener("click", (e) => {
      // 1-tap clear for arrival stop
      const clearBtn = e.target.closest("#timetable-clear-to-stop");
      if (clearBtn) {
        e.stopPropagation();
        delete state.timetableToStop;
        renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
        return;
      }

      // Ignore clicks on the inner .pin-btn or swap
      if (e.target.closest(".pin-btn, .ss-swap")) return;
      const row = e.target.closest("#timetable-from-trigger, #timetable-to-trigger");
      if (!row) return;
      const isFrom = row.id === "timetable-from-trigger";
      const activeLine = state.timetableLine || cfg.lineOrder?.[0] || "Z649";
      const baseDirection = state.timetableDirection || "outbound";
      const direction = resolveDirection(baseDirection, state.settings);
      const dayType = state.timetableDayType || getDayType(new Date(), cfg);
      const scheduleKey = getScheduleKey(activeLine, dayType, direction);
      const visibleStops = getVisibleStops(state, cfg, activeLine, scheduleKey, direction, lineConfig[activeLine]?.referenceStops || []);

      // S1: Build route candidates from the CURRENT direction only.
      // Previously used `getAllStopsOrdered(Object.values(lineData[activeLine]).flat())`
      // which unions ALL directions × day-types → duplicate stop names with
      // different codes appeared in both sheets (e.g. "Milano Molino Dorino M1"
      // existed as both `MD001` (return) and `MD111` (outbound)). Picking the
      // wrong-direction code created an empty FROM/A pair that silently broke
      // expand slices, the empty-column filter, and the smart-default seeding.
      let routeStops = getAllStopsOrdered(lineData[activeLine]?.[scheduleKey] || []);
      if (routeStops.length === 0) {
        // Fallback: union of the same direction's OTHER day-type keys (e.g.
        // when today is sunday but the line serves weekday_outbound only and
        // the user is browsing a different day-type). Same-direction only.
        const sameDirTrips = [];
        for (const dt of ["weekday", "saturday", "sunday"]) {
          if (dt === dayType) continue;
          const k = getScheduleKey(activeLine, dt, direction);
          sameDirTrips.push(...(lineData[activeLine]?.[k] || []));
        }
        routeStops = getAllStopsOrdered(sameDirTrips);
      }

      const trips = lineData[activeLine]?.[scheduleKey] || [];

      if (isFrom) {
        const currentFrom = visibleStops[0] || routeStops[0];
        showStopSheet({
          title: "Fermata di partenza",
          options: routeStops.map(code => ({
            code,
            name: getStopName(code) || code,
            isSelected: code === currentFrom,
            badge: visibleStops.includes(code) ? "preferita" : null
          })),
          onSelect: (code) => {
            const timetableStops = { ...(state.settings?.timetableStops || {}) };
            if (!timetableStops[activeLine]) timetableStops[activeLine] = {};
            for (const dt of ["weekday", "saturday", "sunday"]) {
              const key = getScheduleKey(activeLine, dt, direction);
              const current = timetableStops[activeLine][key] || [...visibleStops];
              timetableStops[activeLine][key] = [code, ...current.filter(c => c !== code)];
            }
            // S5: Re-validate the current A against the new FROM — if A is
            // absent from the new route order or not strictly after FROM,
            // drop it. Prevents "stale A from the previous direction" from
            // causing empty trips and collapsing expand slices.
            if (state.timetableToStop) {
              const fromIdx = routeStops.indexOf(code);
              const toIdx = routeStops.indexOf(state.timetableToStop);
              if (toIdx === -1 || toIdx <= fromIdx) {
                delete state.timetableToStop;
              }
            }
            if (saveSettings) saveSettings({ timetableStops });
            else renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
          }
        });
      } else {
        const fromStop = visibleStops[0] || routeStops[0];
        const fromIdx = routeStops.indexOf(fromStop);

        // S1: A-sheet shows only stops AFTER the current FROM in route order
        // (idx > fromIdx); the FROM stop itself is hidden. The +N min badge
        // is computed against the NEXT trip that serves FROM (previously used
        // trips[0], which may skip FROM entirely → badges vanished).
        const nextTripServingFrom = trips.find((t) => t.stops && t.stops[fromStop] != null) || null;
        const fromMin = nextTripServingFrom?.stops?.[fromStop] ?? null;
        const currentTo = state.timetableToStop || null;

        showStopSheet({
          title: "Fermata di arrivo",
          options: routeStops
            .filter((code, idx) => idx > fromIdx && code !== fromStop)
            .map(code => {
              const arrMin = nextTripServingFrom?.stops?.[code] ?? null;
              const relMin = (fromMin !== null && arrMin !== null) ? arrMin - fromMin : null;
              return {
                code,
                name: getStopName(code) || code,
                isSelected: code === currentTo,
                badge: relMin !== null && relMin > 0 ? `+${relMin} min` : null
              };
            }),
          onSelect: (code) => {
            state.timetableToStop = code;
            renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
          },
          onClear: () => {
            delete state.timetableToStop;
            renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
          }
        });
      }
    });
    // Keyboard support for the <div role="button"> rows.
    container.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const row = e.target.closest("#timetable-from-trigger, #timetable-to-trigger");
      if (!row) return;
      e.preventDefault();
      row.click();
    });
  }

  const swapTrigger = container.querySelector("#timetable-swap-trigger");
  if (swapTrigger && !swapTrigger.__has_click) {
    swapTrigger.__has_click = true;
    swapTrigger.addEventListener("click", () => {
      const currentDir = state.timetableDirection || "outbound";
      state.timetableDirection = currentDir === "outbound" ? "return" : "outbound";
      if (state.timetableToStop) {
        delete state.timetableToStop;
      }
      renderTimetable(state, lineData, lineConfig, cfg, saveSettings);
    });
  }

  const movibusBtn = container.querySelector("#trigger-gtfs-movibus");
  if (movibusBtn && !movibusBtn.__has_click) {
    movibusBtn.__has_click = true;
    movibusBtn.addEventListener("click", () =>
      triggerGhDispatch(movibusBtn, {
        feed: "movibus",
        url: "https://movibus.it/orari-agosto-2026/",
        fileName: "movibus-latest.zip",
        label: "Movibus",
        icon: "🔄"
      })
    );
  }

  const trenordBtn = container.querySelector("#trigger-gtfs-trenord");
  if (trenordBtn && !trenordBtn.__has_click) {
    trenordBtn.__has_click = true;
    trenordBtn.addEventListener("click", () =>
      triggerGhDispatch(trenordBtn, {
        feed: "trenord",
        url: "https://www.trenord.it/gtfs/trenord_gtfs.zip",
        fileName: "trenord-latest.zip",
        label: "Trenord",
        icon: "🚆"
      })
    );
  }
}

// V4: previously the GTFS buttons always showed "✅ avviato!" regardless of
// the HTTP response status. With no `Authorization` header GitHub's
// repository_dispatch endpoint returns 401/404 — a silent-but-failed request.
// Now we throw on non-2xx and surface the real error, plus hide the success
// message when the request itself failed.
async function triggerGhDispatch(btn, { feed, url, fileName, label, icon }) {
  const original = `${icon} Aggiorna GTFS ${label}`;
  btn.disabled = true;
  btn.textContent = `⏳ Avvio aggiornamento GTFS ${label}…`;
  try {
    const res = await fetch("https://api.github.com/repos/adeldev-lab/z-transit/dispatches", {
      method: "POST",
      headers: {
        "Accept": "application/vnd.github.v3+json",
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        event_type: "gtfs-update",
        client_payload: { feed, url, fileName }
      })
    });
    if (!res.ok) {
      // GitHub returns 401 for unauthenticated requests, 404 if the repo or
      // dispatch webhook is disabled. Try to surface a useful hint.
      let detail = `HTTP ${res.status}`;
      try {
        const body = await res.json();
        if (body?.message) detail += ` — ${body.message}`;
      } catch (_) { /* not JSON, ignore */ }
      throw new Error(detail);
    }
    btn.textContent = `✅ GTFS ${label} avviato!`;
    setTimeout(() => {
      btn.disabled = false;
      btn.textContent = original;
    }, 4000);
  } catch (e) {
    console.error(`GTFS update error (${label}):`, e);
    btn.textContent = `⚠️ Avvio fallito — ${e?.message || "errore"} (avvia da GH Actions)`;
    setTimeout(() => {
      btn.disabled = false;
      btn.textContent = original;
    }, 6000);
  }
}

// Edge-case fix: switching line / day-type / direction while the Tabella is
// in edit mode would otherwise leave `state.timetableTempStops` populated
// with the previous context's stop codes; on Salva they'd be written into
// the NEW context's keys. Exit edit mode on any context switch (mirrors
// the Annulla handler).
function exitEditModeIfActive(state) {
  if (!state.timetableEditMode) return;
  state.timetableEditMode = false;
  delete state.timetableTempStops;
}

function showStopSheet({ title, options, onSelect, onClear }) {
  let modal = document.getElementById("stop-sheet-modal");
  if (!modal) {
    modal = document.createElement("div");
    modal.id = "stop-sheet-modal";
    modal.className = "live-customize-modal";
    document.body.appendChild(modal);
  }

  const optionsHtml = options
    .map(
      (opt) => `
        <button type="button" class="stop-sheet-item ${opt.isSelected ? "selected" : ""}" data-code="${escapeHtml(opt.code)}">
          <span>${escapeHtml(opt.name)}</span>
          ${opt.badge ? `<span class="badge ${opt.isSelected ? "accent" : "muted"}">${escapeHtml(opt.badge)}</span>` : ""}
        </button>
      `
    )
    .join("");

  // M1: a "Rimuovi" (clear-current-A) row at the bottom of the A sheet so the
  // user can clear the destination without picking a different one. Only
  // shows when the caller passes `onClear` (the A-sheet branch).
  const clearHtml = typeof onClear === "function"
    ? `<button type="button" class="stop-sheet-item stop-sheet-clear" data-code="" style="color: var(--danger, #ef4444); border-color: color-mix(in srgb, var(--danger, #ef4444) 25%, transparent);"><span>Rimuovi arrivo</span></button>`
    : "";

  modal.innerHTML = `
    <div class="live-customize-sheet">
      <div class="live-customize-header">
        <h3>${escapeHtml(title)}</h3>
        <button type="button" class="live-customize-close" id="close-stop-sheet">×</button>
      </div>
      <div class="live-customize-body" style="max-height: 60vh;">
        <input type="text" class="input stop-sheet-search" id="stop-sheet-search" placeholder="Cerca fermata…" style="margin-bottom: 12px; width: 100%; padding: 8px 12px; border-radius: 8px; border: 1px solid var(--line); background: var(--surface-2, rgba(255,255,255,0.06)); color: var(--text);">
        <div class="stop-sheet-list" id="stop-sheet-list" style="display: flex; flex-direction: column; gap: 6px;">
          ${optionsHtml}
          ${clearHtml}
        </div>
      </div>
    </div>
  `;

  modal.classList.add("open");

  const closeBtn = modal.querySelector("#close-stop-sheet");
  if (closeBtn) {
    closeBtn.onclick = () => modal.classList.remove("open");
  }

  modal.onclick = (e) => {
    if (e.target === modal) modal.classList.remove("open");
  };

  const searchInput = modal.querySelector("#stop-sheet-search");
  if (searchInput) {
    searchInput.oninput = (e) => {
      const q = e.target.value.toLowerCase();
      modal.querySelectorAll(".stop-sheet-item").forEach((item) => {
        const text = item.textContent.toLowerCase();
        item.style.display = text.includes(q) ? "flex" : "none";
      });
    };
  }

  modal.querySelectorAll(".stop-sheet-item").forEach((item) => {
    item.onclick = () => {
      // M1: the clear button has data-code="" and the .stop-sheet-clear class.
      if (item.classList.contains("stop-sheet-clear")) {
        modal.classList.remove("open");
        if (typeof onClear === "function") onClear();
        return;
      }
      modal.classList.remove("open");
      onSelect(item.dataset.code);
    };
  });
}


